import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { State } from '../reducer';
import { DisqualifiedEntry, getDisqualifiedLeaderboard, currentGameUserId, GameId, GamePeriod, getLeaderboard, hasGameAccount, LeaderboardEntry, ModerationStatus, setScoreModeration } from '../api/game.api';

type BoardTab = GamePeriod | 'disqualified';

const TABS: { period: BoardTab; label: string }[] = [
  { period: 'today', label: 'วันนี้' },
  { period: 'week', label: 'สัปดาห์นี้' },
  { period: 'all', label: 'ทั้งหมด' },
  { period: 'disqualified', label: 'คนโกง' },
];

type Board =
  | { status: 'offline' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; entries: LeaderboardEntry[]; me: LeaderboardEntry | null };

export interface PancakeLeaderboardProps {
  game?: GameId;
  /** bump to refetch (e.g. after a score was saved) */
  refreshKey?: number;
}

const MEDAL = ['bg-[#df7836] text-white', 'bg-[#f1b762] text-[#3b2a20]', 'bg-[#e6c9a8] text-[#3b2a20]'];

/** Pancake Tower top-10, one row per player (their best score in the period). */
export default function PancakeLeaderboard({ refreshKey = 0, game = 'pancake-tower' }: PancakeLeaderboardProps) {
  const [period, setPeriod] = useState<BoardTab>('today');
  // isAdminLike includes dev, but this server route authorizes only the admin role.
  const isAdmin = useSelector((state: State) => state.user?.role === 'admin');
  const canModerate = isAdmin && game === 'pancake-tower';
  const [editor, setEditor] = useState<LeaderboardEntry | DisqualifiedEntry | null>(null);
  const [status, setStatus] = useState<ModerationStatus>('clean');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [moderationRefresh, setModerationRefresh] = useState(0);
  const requestEpoch = useRef(0);
  const [disqualified, setDisqualified] = useState<DisqualifiedEntry[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageError, setPageError] = useState(false);
  const moreRequest = useRef(false);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    setEditor(null);
    setSaving(false);
  }, [game, period, canModerate]);
  const [board, setBoard] = useState<Board>(() => (hasGameAccount() ? { status: 'loading' } : { status: 'offline' }));
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const baseId = useId();
  const me = currentGameUserId();

  useEffect(() => {
    const epoch = ++requestEpoch.current;
    if (!hasGameAccount()) {
      setBoard({ status: 'offline' });
      return;
    }
    let alive = true;
    setBoard({ status: 'loading' });
    setNextOffset(null);
    setLoadingMore(false);
    setPageError(false);
    moreRequest.current = false;
    if (period === 'disqualified') {
      setDisqualified([]);
      getDisqualifiedLeaderboard(game, 0, 20)
        .then(data => { if (alive) {
          setDisqualified([...new Map(data.entries.filter(entry => entry.moderationStatus === 'confirmed').map(entry => [entry.userId, entry])).values()]);
          setNextOffset(data.hasMore ? data.nextOffset : null);
          setBoard({ status: 'ready', entries: [], me: null });
        } })
        .catch(() => { if (alive) setBoard({ status: 'error' }); });
      return () => { alive = false; requestEpoch.current = epoch + 1; };
    }
    (game === 'pancake-tower' ? getLeaderboard(period) : getLeaderboard(period, game))
      .then((data) => { if (alive) setBoard({ status: 'ready', entries: (data?.entries ?? []).slice(0, 10), me: data?.me ?? null }); })
      .catch(() => { if (alive) setBoard({ status: 'error' }); });
    return () => { alive = false; requestEpoch.current = epoch + 1; };
  }, [period, refreshKey, game, moderationRefresh]);

  async function loadMore() {
    if (period !== 'disqualified' || nextOffset === null || moreRequest.current) return;
    const epoch = requestEpoch.current;
    moreRequest.current = true;
    setLoadingMore(true);
    setPageError(false);
    try {
      const data = await getDisqualifiedLeaderboard(game, nextOffset, 20);
      if (epoch !== requestEpoch.current) return;
      setDisqualified(previous => [...new Map([...previous, ...data.entries.filter(entry => entry.moderationStatus === 'confirmed')].map(entry => [entry.userId, entry])).values()]);
      setNextOffset(data.hasMore ? data.nextOffset : null);
    } catch {
      if (epoch === requestEpoch.current) setPageError(true);
    } finally {
      if (epoch === requestEpoch.current) { moreRequest.current = false; setLoadingMore(false); }
    }
  }

  function onTabKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const keys: Record<string, number> = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: TABS.length - 1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const next = (keys[event.key] + TABS.length) % TABS.length;
    setPeriod(TABS[next].period);
    tabRefs.current[next]?.focus();
  }

  async function saveModeration() {
    if (!canModerate || !editor?.scoreId || saving) return;
    const epoch = requestEpoch.current;
    setSaving(true);
    setNotice('');
    try {
      await setScoreModeration(editor.scoreId, status, reason.trim() || undefined);
      if (epoch !== requestEpoch.current) return;
      setEditor(null);
      setNotice('อัปเดตสถานะคะแนนแล้ว');
      // Read back the server leaderboard; never alter score order or rank locally.
      setModerationRefresh(value => value + 1);
    } catch {
      if (epoch === requestEpoch.current) setNotice('อัปเดตสถานะไม่สำเร็จ — ตรวจสอบสิทธิ์แล้วลองอีกครั้ง');
    } finally {
      if (epoch === requestEpoch.current) setSaving(false);
    }
  }

  function renderEntry(entry: LeaderboardEntry, mine: boolean) {
    const status = entry.moderationStatus ?? 'clean';
    const flagged = status !== 'clean';
    return (
      <li
        key={entry.userId}
        aria-current={mine ? 'true' : undefined}
        className={`flex min-h-[44px] flex-wrap items-center gap-2 rounded-2xl px-2 py-1.5 sm:gap-3 sm:px-3 ${
          mine ? 'bg-[#ffe3c7] ring-2 ring-[#df7836]' : 'bg-white/70'
        }`}
      >
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-extrabold tabular-nums ${MEDAL[entry.rank - 1] ?? 'bg-[#f4e9da] text-[#644a3a]'}`}>
          {entry.rank}
        </span>
        <span className="flex min-w-0 flex-1 items-center text-sm font-semibold text-[#3b2a20]">
          <span className="truncate">{entry.name}</span>
          {status !== 'clean' && <span className="ml-1 shrink-0 whitespace-nowrap rounded-full bg-[#f4e9da] px-1.5 py-0.5 text-[11px] font-bold text-[#b94716]">{status === 'confirmed' ? 'โกง' : 'รอตรวจสอบ'}</span>}
          {mine && <span className="ml-1 font-extrabold text-[#b94716]">(คุณ)</span>}
        </span>
        <span className="shrink-0 text-right leading-tight">
          <span className={`block text-base font-extrabold tabular-nums text-[#3b2a20] ${flagged ? 'line-through opacity-60' : ''}`}>{entry.score}</span>
          <span className="block text-[11px] text-[#7a5b48]">{game === 'witch-flight' ? `${entry.score} คะแนน` : `${entry.layers} ชั้น`}</span>
        </span>
        {canModerate && Number.isSafeInteger(entry.scoreId) && (entry.scoreId ?? 0) > 0 && (
          <button type="button" disabled={saving} aria-label={`ตรวจสอบคะแนนของ ${entry.name}`}
            className="min-h-[44px] basis-full rounded-lg px-2 text-right text-xs font-bold text-[#b94716] focus-visible:outline focus-visible:outline-2 sm:basis-auto"
            onClick={() => { setEditor(entry); setStatus(entry.moderationStatus ?? 'clean'); setReason(''); setNotice(''); }}>
            ตรวจสอบ
          </button>
        )}
      </li>
    );
  }

  const panelId = `${baseId}-panel`;
  const note = 'px-2 py-6 text-center text-sm text-[#5a4234]';

  return (
    <section aria-labelledby={`${baseId}-title`} className="mx-auto mt-4 w-full max-w-[460px] rounded-[24px] bg-[#fff8ee] p-3 ring-1 ring-[#f1dcc2] sm:p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2 px-1">
        <h3 id={`${baseId}-title`} className="text-lg font-extrabold text-[#3b2a20]">อันดับ</h3>
      </div>

      <div role="tablist" aria-label="ช่วงเวลาของอันดับ" className="grid grid-cols-4 gap-1 rounded-full bg-[#f4e9da] p-1">
        {TABS.map((tab, index) => {
          const selected = period === tab.period;
          return (
            <button
              key={tab.period}
              type="button"
              role="tab"
              id={`${baseId}-${tab.period}`}
              ref={(node) => { tabRefs.current[index] = node; }}
              aria-selected={selected}
              aria-controls={panelId}
              tabIndex={selected ? 0 : -1}
              onClick={() => setPeriod(tab.period)}
              onKeyDown={(event) => onTabKey(event, index)}
              className={`min-h-[44px] min-w-0 rounded-full px-1 text-xs font-bold transition-colors sm:px-2 sm:text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#3b2a20] ${
                selected ? 'bg-[#b94716] text-[#fffaf2] shadow-sm' : 'text-[#644a3a] hover:bg-[#efdcc4]'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div id={panelId} role="tabpanel" aria-labelledby={`${baseId}-${period}`} aria-busy={board.status === 'loading'} tabIndex={0} className="mt-2 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-[#df7836]">
        {board.status === 'offline' && <p className={note}>เข้าสู่ระบบเพื่อดูอันดับและบันทึกคะแนน</p>}
        {board.status === 'loading' && <p className={note}>{period === 'disqualified' ? 'กำลังโหลดรายชื่อ…' : 'กำลังโหลดอันดับ…'}</p>}
        {board.status === 'error' && (period === 'disqualified'
          ? <div className={note}><p>โหลดรายชื่อไม่ได้</p><button type="button" className="min-h-[44px] px-3 font-bold" onClick={() => setModerationRefresh(value => value + 1)}>ลองใหม่</button></div>
          : <p className={note}>โหลดอันดับไม่ได้ ลองใหม่อีกครั้งภายหลัง</p>)}
        {period === 'disqualified' && board.status === 'ready' && disqualified.length === 0 && <p className={note}>ยังไม่มีผู้เล่นที่ยืนยันว่าโกง</p>}
        {period !== 'disqualified' && board.status === 'ready' && board.entries.length === 0 && <p className={note}>ยังไม่มีใครทำคะแนน — มาเป็นคนแรกกันไหม?</p>}
        {period === 'disqualified' && board.status === 'ready' && (
          <ul className="flex flex-col gap-1">
            {disqualified.map(entry => (
              <li key={entry.userId} className="flex min-h-[44px] min-w-0 flex-wrap items-center gap-2 rounded-2xl bg-white/70 px-2 py-1.5">
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#3b2a20]">{entry.name}</span>
                <span className="shrink-0 rounded-full bg-[#f4e9da] px-1.5 py-0.5 text-[11px] font-bold text-[#b94716]">โกง</span>
                {canModerate && Number.isSafeInteger(entry.scoreId) && entry.scoreId > 0 && (
                  <button type="button" disabled={saving} aria-label={`ตรวจสอบคะแนนของ ${entry.name}`}
                    className="min-h-[44px] basis-full rounded-lg px-2 text-right text-xs font-bold text-[#b94716] focus-visible:outline focus-visible:outline-2 sm:basis-auto"
                    onClick={() => { setEditor(entry); setStatus('confirmed'); setReason(''); setNotice(''); }}>ตรวจสอบ</button>
                )}
              </li>
            ))}
          </ul>
        )}
        {board.status === 'ready' && board.entries.length > 0 && (
          <ol className="flex flex-col gap-1">
            {board.entries.map(entry => renderEntry(entry, entry.userId === me))}
          </ol>
        )}
        {board.status === 'ready' && board.me && board.me.rank > 10 && (
          <>
            <hr className="my-3 border-[#e6c9a8]" />
            <ol start={board.me.rank} aria-label="อันดับของคุณ" className="flex flex-col gap-1">
              {renderEntry(board.me, true)}
            </ol>
          </>
        )}
        {period === 'disqualified' && board.status === 'ready' && (<>
          {pageError && <p role="alert" className={note}>โหลดรายชื่อไม่ได้ ลองโหลดเพิ่มเติมอีกครั้ง</p>}
          {nextOffset !== null && <button type="button" disabled={loadingMore} onClick={() => void loadMore()} className="mt-2 min-h-[44px] w-full rounded-xl bg-[#f4e9da] px-3 text-sm font-bold text-[#b94716] disabled:opacity-60">{loadingMore ? 'กำลังโหลด…' : 'โหลดเพิ่มเติม'}</button>}
        </>)}
      </div>
      {canModerate && editor && (
        <form aria-label={`ตรวจสอบคะแนนของ ${editor.name}`} className="mt-3 space-y-2 rounded-2xl bg-[#f4e9da] p-3 text-sm text-[#3b2a20]"
          onSubmit={event => { event.preventDefault(); void saveModeration(); }}>
          <p className="break-words font-bold">ตรวจสอบคะแนนของ {editor.name}</p>
          <p className="text-xs">รอตรวจสอบไม่ใช่ข้อยืนยันว่าโกง · ยืนยันโดยผู้ดูแลเท่านั้น</p>
          {period === 'disqualified' && <p className="text-xs">การล้างสถานะรอบนี้อาจไม่คืนสิทธิ์ หากยังมีรอบอื่นที่ยืนยันว่าโกง ผู้เล่นจะยังอยู่ในรายชื่อนี้</p>}
          <label className="block">สถานะคะแนน
            <select autoFocus value={status} disabled={saving} onChange={event => setStatus(event.target.value as ModerationStatus)} className="mt-1 block min-h-[44px] w-full rounded-lg bg-white px-2">
              <option value="clean">ปกติ</option><option value="review">รอการตรวจสอบ</option><option value="confirmed">ยืนยันว่าโกง</option>
            </select>
          </label>
          <label className="block">เหตุผล (ไม่บังคับ)
            <input value={reason} disabled={saving} maxLength={32} onChange={event => setReason(event.target.value)} className="mt-1 block min-h-[44px] w-full rounded-lg px-2" />
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="min-h-[44px] rounded-lg bg-[#b94716] px-3 font-bold text-white disabled:opacity-60">{saving ? 'กำลังบันทึก…' : 'บันทึกสถานะ'}</button>
            <button type="button" disabled={saving} onClick={() => setEditor(null)} className="min-h-[44px] rounded-lg px-3">ยกเลิก</button>
          </div>
        </form>
      )}
      {notice && <p role="status" aria-live="polite" className="mt-2 rounded-lg bg-[#f4e9da] p-2 text-sm text-[#3b2a20]">{notice}</p>}
      {period !== 'disqualified' && <p className="mt-2 px-1 text-[12px] text-[#7a5b48]">อันดับคิดจากคะแนนดีที่สุดของแต่ละคน</p>}
    </section>
  );
}
